import React from 'react'

export interface PillTabItem<T extends string = string> {
  key: T
  label: string
  /** Optional icon/emoji prefix — rendered before label */
  icon?: string
  disabled?: boolean
}

interface PillTabsProps<T extends string = string> {
  tabs: PillTabItem<T>[]
  active: T
  onChange: (key: T) => void
  /** Dims inactive tabs while a fetch is in progress */
  loading?: boolean
  size?: 'sm' | 'md'
  style?: React.CSSProperties
  className?: string
}

export function PillTabs<T extends string = string>({
  tabs, active, onChange, loading = false, size = 'md', style, className,
}: PillTabsProps<T>) {
  const pad  = size === 'sm' ? '0.2rem 0.6rem'  : '0.3rem 0.85rem'
  const font = size === 'sm' ? '0.72rem'        : '0.78rem'

  return (
    <div
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.25rem',
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border)',
        borderRadius: '10px',
        padding: '0.2rem',
        ...style,
      }}
    >
      {tabs.map(tab => {
        const isActive = tab.key === active
        return (
          <button
            key={tab.key}
            onClick={() => { if (!tab.disabled && !loading) onChange(tab.key) }}
            disabled={tab.disabled}
            style={{
              padding: pad,
              fontSize: font,
              fontWeight: isActive ? 700 : 500,
              borderRadius: '8px',
              border: 'none',
              cursor: tab.disabled ? 'not-allowed' : loading && !isActive ? 'wait' : 'pointer',
              background: isActive ? 'var(--bg-card)' : 'transparent',
              color: isActive ? 'var(--text-primary)' : 'var(--text-muted)',
              boxShadow: isActive ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              opacity: loading && !isActive ? 0.5 : tab.disabled ? 0.4 : 1,
              transition: 'background 0.15s, color 0.15s, opacity 0.15s',
              whiteSpace: 'nowrap',
              lineHeight: 1.4,
            }}
          >
            {tab.icon && <span style={{ marginRight: '0.25rem' }}>{tab.icon}</span>}
            {tab.label}
          </button>
        )
      })}
    </div>
  )
}
